import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RolePermission } from '../types';

const Header: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const can = (permission: RolePermission) => !!user?.permissions?.includes(permission);
  const canCreate = can('create_discussions');
  const canViewDraftQueue = can('view_unpublished_discussions');
  const canAccessOps = can('access_staff_operations');
  const canReviewReports = can('manage_weather_reports');
  const canInvite = can('invite_users');
  const canManageUsers = can('view_users');
  const isOwner = user?.role === 'owner';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="site-header">
      <div className="container">
        <div className="header-top">
          <div>
            <Link to="/" className="brand-link">
              <h1 className="site-title">GLSN Ops Dashboard</h1>
            </Link>
            <p className="site-tagline">Operations hub for internal discussions, severe weather reports, and core staff workflows</p>
          </div>

          <div className="header-user-block">
            {isAuthenticated ? (
              <>
                <p className="user-meta">Signed in as <strong>{user?.username}</strong> <span className={`role-pill role-${user?.role}`}>{user?.role}</span></p>
                <div className="header-actions">
                  <Link to="/settings" className="btn btn-secondary">Settings</Link>
                  {canCreate && <Link to="/compose" className="btn btn-primary">New discussion</Link>}
                  {canViewDraftQueue && <Link to="/staff/drafts" className="btn btn-secondary">Draft queue</Link>}
                  {canAccessOps && <Link to="/staff/ops-center" className="btn btn-secondary">Ops center</Link>}
                  {canInvite && <Link to="/invite-users" className="btn btn-secondary">Invite users</Link>}
                  {canManageUsers && <Link to="/manage-users" className="btn btn-secondary">Manage users</Link>}
                  {isOwner && <Link to="/manage-permissions" className="btn btn-secondary">Role permissions</Link>}
                  <button onClick={logout} className="btn btn-secondary" type="button">Logout</button>
                </div>
              </>
            ) : (
              <div className="header-actions">
                <Link to="/register" className="btn btn-secondary">Register</Link>
                <Link to="/login" className="btn btn-primary">Login</Link>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search dashboard discussions and operational notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <div className="header-nav-row">
          <div className="header-link-group">
            <Link to="/report" className="header-nav-link">Report weather</Link>
            <Link to="/status" className="header-nav-link">Status</Link>
          </div>
          {(canReviewReports || canAccessOps) && (
            <div className="header-link-group">
              {canReviewReports && <Link to="/staff/reports" className="header-nav-link">Reports inbox</Link>}
              {canViewDraftQueue && <Link to="/staff/drafts" className="header-nav-link">Draft queue</Link>}
              {canAccessOps && <Link to="/staff/ops-center" className="header-nav-link">Ops center</Link>}
              {/* Templates removed — using official GLSN MD format */}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
